import * as Sentry from '@sentry/react'
import axios from 'axios'
import dayjs from 'dayjs'

import { getAuthorizationHeader } from '../services/api/authHeader'

type getUrlPath = { identityId?: string }

type getUrlPathFunc = (path: getUrlPath) => string

type getUrlType = getUrlPathFunc | string

type UploadResponse = {
  key: string
  url?: string
}

const resolveObjectKey = (s3ObjectKey: getUrlType) => {
  if (typeof s3ObjectKey === 'function') {
    return s3ObjectKey({})
  }

  return s3ObjectKey
}

const getFileNameFromKey = (objectKey: string) =>
  objectKey.split('/').filter(Boolean).pop() || 'upload'

const getFileExtension = (fileName: string) => {
  if (!fileName) return ''

  const regexResult = fileName.match(/\.([^.]+)$/)
  const fileExtension = regexResult?.length ? regexResult[1] : ''
  return fileExtension
}

const normalizeUploadFile = (file: File | Uint8Array, objectKey: string) => {
  const fileName = getFileNameFromKey(objectKey)

  if (file instanceof File) {
    return file
  }

  const arrayBuffer = file.buffer.slice(
    file.byteOffset,
    file.byteOffset + file.byteLength
  ) as ArrayBuffer

  return new File([arrayBuffer], fileName, { type: 'application/pdf' })
}

const uploadFileToS3 = async (file: File | Uint8Array, objectKey: string) => {
  try {
    const payload = new FormData()
    const uploadableFile = normalizeUploadFile(file, objectKey)

    payload.append('file', uploadableFile)
    payload.append('key', objectKey)

    const headers = await getAuthorizationHeader()
    const baseUrl = import.meta.env.VITE_DRIVER_API_BASE_URL
    const response = await axios.post<UploadResponse>(
      `${baseUrl}/v1/uploads`,
      payload,
      {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data',
        },
      }
    )

    return response.data.key || objectKey
  } catch (error) {
    console.log('Error on uploadFileToS3', error)

    Sentry.captureException(error)
    throw error
  }
}

const checkFileExists = async (s3ObjectKey: getUrlType) => {
  const objectKey = resolveObjectKey(s3ObjectKey)

  if (!objectKey) {
    throw new Error('Missing object key')
  }

  return objectKey
}

const putToS3 = (
  file: File,
  folder: string,
  label: string
): Promise<string> => {
  const todayDate = dayjs().format('YYYY-MM-DD')
  const fileExtension = getFileExtension(file.name)
  const fileName = `${label}_${todayDate}.${fileExtension}`
  const objectKey = `${folder}/${fileName}`

  return uploadFileToS3(file, objectKey)
}

const uint8ArrayToS3 = async (
  file: Uint8Array,
  folder: string,
  fileName: string
) => {
  const objectKey = `${folder}/${fileName}.pdf`
  return uploadFileToS3(file, objectKey)
}

const getFileFromS3 = async (
  s3ObjectKey: getUrlType
): Promise<{ href: string } | null> => {
  try {
    const objectKey = resolveObjectKey(s3ObjectKey)

    if (!objectKey) {
      throw new Error('Missing object key')
    }

    const headers = await getAuthorizationHeader()
    const baseUrl = import.meta.env.VITE_DRIVER_API_BASE_URL
    const response = await axios.get<UploadResponse>(
      `${baseUrl}/v1/uploads?key=${encodeURIComponent(objectKey)}`,
      {
        headers,
      }
    )

    return {
      href: response.data.url || response.data.key || '',
    }
  } catch (error) {
    console.log('Error on getFileFromS3', error)
    Sentry.captureException(error)
    return null
  }
}

const getInsuranceFromS3 = async (key: number) => {
  const env = import.meta.env.VITE_ENVIRONMENT_NAME

  const s3ObjectKey = `${import.meta.env.VITE_INSURANCE_CLOUDFRONT_URL}${env}/${key}/insurance_slip/document.pdf`

  return s3ObjectKey
}

const getProtectedS3 = async (
  key: string
): Promise<null | string | URL> => {
  const s3ObjectKey = `protected/${key}`

  return getFileFromS3(s3ObjectKey)
}

export {
  checkFileExists,
  getFileFromS3,
  getInsuranceFromS3,
  getProtectedS3,
  putToS3,
  uint8ArrayToS3,
}
